// src/pages/motolivraison/commercants.page.jsx
//
// Liste des inscriptions commerçant (User.commercantProfile) avec actions admin.
// À ne pas confondre avec commerces.page.jsx qui gère les boutiques (collection Commerce).

import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Modal, message, Button, Card, Row, Col, Tag, Space,
  Breadcrumb, Avatar, DatePicker,
} from 'antd';
import {
  CheckCircleOutlined, StopOutlined, PlayCircleOutlined,
  ShopOutlined, RightOutlined, ClockCircleOutlined,
  UserOutlined, EyeOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { motolivraisonService } from '../../common/services';
import { formatPhoneCI } from '../../common/helper';
import {
  LOCALE_TABLE, PAGINATION_TABLE, BTN_ACTION, COULEURS_ACTION, cmpTexte, CarteStat,
} from './table.helpers';
import './css/motolivraison-table.css';

const Commercants = () => {
  const [commercants, setCommercants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ dates: null });
  const navigate = useNavigate();

  const stats = {
    total: commercants.length,
    pending: commercants.filter(c => c.commercantProfile?.status === 'pending').length,
    approved: commercants.filter(c => c.commercantProfile?.status === 'approved').length,
    suspended: commercants.filter(c => c.commercantProfile?.status === 'suspended').length,
  };

  const fetchCommercants = useCallback(async () => {
    setLoading(true);
    try {
      const data = await motolivraisonService.listCommercants({
        dateDebut: filters.dates?.[0] ? filters.dates[0].format('YYYY-MM-DD') : undefined,
        dateFin: filters.dates?.[1] ? filters.dates[1].format('YYYY-MM-DD') : undefined,
        // Tri et filtres de colonnes portent sur l'ensemble, pas sur la 1re page serveur
        limit: 1000,
      });
      setCommercants(data?.commercants || []);
    } catch (e) {
      message.error('Erreur lors du chargement des commerçants');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchCommercants();
  }, [fetchCommercants]);

  const handleAction = async (action, commercant) => {
    Modal.confirm({
      title: `${action === 'validate' ? 'Approuver' : action === 'suspend' ? 'Suspendre' : 'Réactiver'} ce commerçant ?`,
      content: `${commercant.firstName} ${commercant.lastName} (${commercant.phone})`,
      onOk: async () => {
        try {
          if (action === 'validate') await motolivraisonService.validateCommercant(commercant._id);
          else if (action === 'suspend') await motolivraisonService.suspendCommercant(commercant._id);
          else if (action === 'reactivate') await motolivraisonService.reactivateCommercant(commercant._id);
          message.success('Action effectuée');
          fetchCommercants();
        } catch (e) {
          message.error(e?.message || 'Erreur');
        }
      },
    });
  };

  const statusColor = (s) => ({ pending: 'orange', approved: 'green', suspended: 'red' })[s] || 'default';
  const statusLabel = (s) => ({ pending: 'En attente', approved: 'Approuvé', suspended: 'Suspendu' })[s] || s || '—';

  const columns = [
    {
      title: '', key: 'avatar', width: 60, align: 'center',
      render: () => <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#fa8c16' }} />,
    },
    {
      title: 'Commerçant', key: 'nom', width: 240, ellipsis: true,
      sorter: (a, b) => cmpTexte(`${a.firstName} ${a.lastName}`, `${b.firstName} ${b.lastName}`),
      render: (_, r) => <span style={{ fontWeight: 600 }}>{`${r.firstName || ''} ${r.lastName || ''}`.trim() || '—'}</span>,
    },
    {
      title: 'Téléphone', key: 'tel', width: 150,
      render: (_, r) => <span style={{ whiteSpace: 'nowrap' }}>{formatPhoneCI(r.phone)}</span>,
    },
    {
      title: 'Email', dataIndex: 'email', key: 'email', width: 200, ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: 'Inscrit le', key: 'date', width: 160,
      defaultSortOrder: 'descend',
      sorter: (a, b) =>
        new Date(a.commercantProfile?.registeredAt || 0) - new Date(b.commercantProfile?.registeredAt || 0),
      render: (_, r) => {
        const d = r.commercantProfile?.registeredAt;
        return d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—';
      },
    },
    {
      title: 'Statut', key: 'status', width: 130,
      filters: ['pending', 'approved', 'suspended'].map(v => ({ text: statusLabel(v), value: v })),
      onFilter: (value, r) => r.commercantProfile?.status === value,
      render: (_, r) => {
        const s = r.commercantProfile?.status;
        return <Tag color={statusColor(s)}>{statusLabel(s)}</Tag>;
      },
    },
    {
      title: 'Actions', key: 'actions', width: 300, align: 'center', fixed: 'right',
      render: (_, r) => {
        const status = r.commercantProfile?.status;
        return (
          <Space size={6}>
            <Button
              size="small"
              icon={<EyeOutlined />}
              style={BTN_ACTION(COULEURS_ACTION.voir)}
              onClick={() => navigate(`/dashboard/motolivraison/commercants/${r._id}`)}
            >
              Détails
            </Button>
            {status === 'pending' && (
              <Button size="small" icon={<CheckCircleOutlined />} style={BTN_ACTION(COULEURS_ACTION.valider)}
                onClick={() => handleAction('validate', r)}>
                Approuver
              </Button>
            )}
            {status === 'approved' && (
              <Button size="small" icon={<StopOutlined />} style={BTN_ACTION(COULEURS_ACTION.danger)}
                onClick={() => handleAction('suspend', r)}>
                Suspendre
              </Button>
            )}
            {status === 'suspended' && (
              <Button size="small" icon={<PlayCircleOutlined />} style={BTN_ACTION(COULEURS_ACTION.valider)}
                onClick={() => handleAction('reactivate', r)}>
                Réactiver
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item>Commerçants</Breadcrumb.Item>
        </Breadcrumb>

        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={6}><CarteStat label="Total" value={stats.total} icon={<ShopOutlined />} /></Col>
          <Col xs={12} sm={6}><CarteStat label="En attente" value={stats.pending} icon={<ClockCircleOutlined />} color="#fa8c16" /></Col>
          <Col xs={12} sm={6}><CarteStat label="Approuvés" value={stats.approved} icon={<CheckCircleOutlined />} color="#52c41a" /></Col>
          <Col xs={12} sm={6}><CarteStat label="Suspendus" value={stats.suspended} icon={<StopOutlined />} color="#f5222d" /></Col>
        </Row>

        <Card title="Commerçants MotoLivraison">
          <Row gutter={12} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12}>
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder={['Inscrit du', 'au']}
                value={filters.dates}
                onChange={(v) => setFilters({ ...filters, dates: v })}
              />
            </Col>
          </Row>
          <Table
            className="moto-table"
            columns={columns}
            dataSource={commercants.map(c => ({ ...c, key: c._id }))}
            loading={loading}
            size="middle"
            locale={LOCALE_TABLE}
            pagination={PAGINATION_TABLE('commerçant')}
            scroll={{ x: 1240 }}
          />
        </Card>
      </div>
    </div>
  );
};

export default Commercants;
